import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import { WeekNavigator } from "../components/WeekNavigator";
import { RecipePicker } from "../components/RecipePicker";
import type {
  MealPlanEntryIn,
  MealPlanEntryPublic,
  MealPlanEntryUpdate,
  MealPlanPublic,
  RecipeListItem,
} from "../lib/types";
import {
  DAY_NAMES,
  formatDayDateOnly,
  formatDayLabel,
  thisMondayIso,
} from "../lib/week";

function isValidIsoDate(iso: string | undefined): iso is string {
  return typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

export function PlanPage() {
  const { week_start } = useParams<{ week_start?: string }>();

  // Guard against bad URLs — send them home to this week.
  if (week_start !== undefined && !isValidIsoDate(week_start)) {
    return <Navigate to="/plan" replace />;
  }

  // Without a param, fall through to this week's Monday.
  const weekStartIso = week_start ?? thisMondayIso();

  return <PlanWeek weekStartIso={weekStartIso} />;
}

function PlanWeek({ weekStartIso }: { weekStartIso: string }) {
  const qc = useQueryClient();
  const [pickerDay, setPickerDay] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<MealPlanPublic>({
    queryKey: ["meal-plan", weekStartIso],
    queryFn: () =>
      api.get<MealPlanPublic>(`/api/meal-plans/${weekStartIso}`),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["meal-plan", weekStartIso] });

  const addEntry = useMutation({
    mutationFn: (payload: MealPlanEntryIn) =>
      api.post<MealPlanEntryPublic>(
        `/api/meal-plans/${weekStartIso}/entries`,
        payload,
      ),
    onSuccess: () => {
      invalidate();
      setPickerDay(null);
      setError(null);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Failed to add this entry.");
    },
  });

  const updateEntry = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MealPlanEntryUpdate }) =>
      api.patch<MealPlanEntryPublic>(`/api/meal-plans/entries/${id}`, body),
    onSuccess: () => invalidate(),
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Failed to update servings.");
    },
  });

  const deleteEntry = useMutation({
    mutationFn: (id: number) =>
      api.delete<void>(`/api/meal-plans/entries/${id}`),
    onSuccess: () => invalidate(),
  });

  const entriesByDay: Record<number, MealPlanEntryPublic[]> = {
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
  };
  (data?.entries ?? []).forEach((entry) => {
    if (entry.day >= 0 && entry.day <= 6) {
      entriesByDay[entry.day].push(entry);
    }
  });

  const handlePick = (recipe: RecipeListItem) => {
    if (pickerDay === null) return;
    addEntry.mutate({
      recipe_id: recipe.id,
      day: pickerDay,
      servings: recipe.servings,
    });
  };

  const totalEntries = data?.entries.length ?? 0;

  return (
    <Shell>
      <section className="mb-16 grid grid-cols-12 gap-8 border-b border-ink/80 pb-14">
        <div
          className="col-span-12 md:col-span-3 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="label mb-3 text-ink-walnut">Section</div>
          <div className="label text-sienna">N° 03</div>
          <div className="label mt-1 text-ink-walnut">
            {totalEntries} {totalEntries === 1 ? "entry" : "entries"}
          </div>
        </div>
        <div
          className="col-span-12 md:col-span-9 animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          <h1 className="display-1">
            The <span className="display-italic">Plan</span>
          </h1>
          <p className="mt-8 max-w-xl font-body text-xl italic leading-snug text-ink-walnut">
            Seven suppers pressed against the week — what we'll make, in which
            order, and for how many at the table.
          </p>
        </div>
      </section>

      <section
        className="mb-14 animate-fade-up"
        style={{ animationDelay: "220ms" }}
      >
        <WeekNavigator weekStartIso={weekStartIso} basePath="/plan" />
      </section>

      {isLoading && (
        <p className="label animate-fade-in text-ink-muted">
          Turning to this week…
        </p>
      )}

      {isError && (
        <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna">
          This week could not be opened. Try again in a moment.
        </div>
      )}

      {error && (
        <div className="mb-10 border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
          {error}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 gap-px bg-ink/15 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {DAY_NAMES.map((name, idx) => {
            const entries = entriesByDay[idx] ?? [];
            const dateOnly = formatDayDateOnly(weekStartIso, idx);
            return (
              <article
                key={name}
                className="group flex min-h-[220px] flex-col bg-parchment p-6 transition-colors duration-300 hover:bg-parchment-deep/60 animate-fade-up"
                style={{ animationDelay: `${260 + idx * 40}ms` }}
              >
                <header className="mb-4 flex items-baseline justify-between gap-3">
                  <div>
                    <div className="font-display text-2xl font-light leading-none text-ink">
                      {name}
                    </div>
                    <div className="label mt-1 text-ink-muted">
                      {dateOnly}
                    </div>
                  </div>
                  <div className="label text-sienna">
                    D{String(idx + 1).padStart(2, "0")}
                  </div>
                </header>

                <span className="hairline mb-4" />

                {entries.length === 0 && (
                  <p className="mb-6 font-body italic text-ink-muted">
                    Nothing set.
                  </p>
                )}

                {entries.length > 0 && (
                  <ul className="mb-6 space-y-4">
                    {entries.map((entry) => (
                      <li
                        key={entry.id}
                        className="group/entry border-l-2 border-sienna/50 pl-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-display text-lg font-light leading-snug text-ink">
                            {entry.recipe_title}
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteEntry.mutate(entry.id)}
                            className="label text-ink-muted opacity-0 transition-all duration-200 hover:text-sienna group-hover/entry:opacity-100"
                            aria-label={`Remove ${entry.recipe_title} from ${name}`}
                          >
                            ×
                          </button>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <label
                            className="label text-ink-walnut"
                            htmlFor={`srv-${entry.id}`}
                          >
                            Serves
                          </label>
                          <input
                            id={`srv-${entry.id}`}
                            type="number"
                            min={1}
                            max={50}
                            value={entry.servings}
                            onChange={(e) => {
                              const next = parseInt(e.target.value || "1", 10);
                              if (next >= 1 && next !== entry.servings) {
                                updateEntry.mutate({
                                  id: entry.id,
                                  body: { servings: next },
                                });
                              }
                            }}
                            className="w-14 border-0 border-b border-ink/25 bg-transparent py-1 text-center font-display text-lg font-light text-ink focus:border-ink focus:outline-none"
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-auto">
                  <button
                    type="button"
                    onClick={() => setPickerDay(idx)}
                    className="label ink-link text-ink"
                  >
                    + Add a dish
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="mt-28 flex items-center gap-6">
        <span className="hairline flex-1" />
        <Ornament className="h-4 w-20 text-sienna" />
        <span className="hairline flex-1" />
      </div>

      {pickerDay !== null && (
        <RecipePicker
          dayLabel={formatDayLabel(weekStartIso, pickerDay)}
          onPick={handlePick}
          onClose={() => setPickerDay(null)}
        />
      )}
    </Shell>
  );
}
